import React, { useState } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import { Formik } from 'formik'
import { FormControlLabel, Checkbox, Grid, Button } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import messages from '../../../messages'
import ProductDetailsLink from '@src/routes/SemiConfigurators/components/ProductDetailsLink'
import { semiConfigContext, ProductDetails } from '../Shrna'
import { useRouter, shrnaRoute } from '@src/routes'
import TableContainer from '@material-ui/core/TableContainer'
import { cloneDeep } from 'lodash'
import { SpecificationMessages, ActiveLinkType } from '../Enum'
import { useShrnaDropDownListLazyQuery } from '@src/queries/ShrnaSpecificationDetailsQuery.generated'
import { CloneInformation, initialState } from '../Reducer'
import PaginationForShrna from './PaginationForShrna'
import CircleLoader from '@src/components/CircleLoader/CircleLoader'
import { SemiConfiguratorValues } from '../../../utils/constants'

interface Column {
  id:
    | 'TRCCloneId'
    | 'Validation'
    | 'GeneSymbol'
    | 'GeneID'
    | 'RefSeq'
    | 'Species'
    | 'Region'
    | 'EmptyLabel'
  label: any
  minWidth?: number
  align?: 'right'
  format?: (value: number) => string
}

const columns: Column[] = [
  {
    id: 'TRCCloneId',
    label: (
      <FormattedMessage
        {...messages.SEMI_CONF_SHRNA_TRC_CLONE_ID_LABEL_shrna}
      />
    ),
    minWidth: 100,
  },
  {
    id: 'Validation',
    label: (
      <FormattedMessage {...messages.SEMI_CONF_SHRNA_VALIDATION_LABEL_shrna} />
    ),
    minWidth: 100,
  },
  {
    id: 'GeneSymbol',
    label: (
      <FormattedMessage {...messages.SEMI_CONF_SHRNA_GENE_SYMBOL_LABEL_shrna} />
    ),
    minWidth: 80,
  },
  {
    id: 'GeneID',
    label: (
      <FormattedMessage {...messages.SEMI_CONF_SHRNA_GENE_ID_LABEL_shrna} />
    ),
    minWidth: 70,
  },
  {
    id: 'RefSeq',
    label: (
      <FormattedMessage {...messages.SEMI_CONF_SHRNA_REF_SEQ_ID_LABEL_shrna} />
    ),
    minWidth: 60,
  },
  {
    id: 'Species',
    label: (
      <FormattedMessage {...messages.SEMI_CONF_SHRNA_SPECIES_LABEL_shrna} />
    ),
    minWidth: 50,
  },
  {
    id: 'Region',
    label: (
      <FormattedMessage {...messages.SEMI_CONF_SHRNA_REGION_LABEL_shrna} />
    ),
    minWidth: 40,
  },
  { id: 'EmptyLabel', label: ' ', minWidth: 60 },
]

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      width: '100%',
      paddingRight: 30,
      paddingLeft: 30,
    },

    container: {
      maxHeight: 440,
    },
    fontBold: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightMedium,
    },
    paper: {
      padding: theme.spacing(2),
      textAlign: 'justify',
    },
    lightOrangeFont: {
      backgroundColor: '#ff8d00',
      color: '#ffffff',
      fontSize: theme.typography.pxToRem(10),
      padding: theme.spacing(1),
    },
    lightGreyFont: {
      fontSize: theme.typography.pxToRem(12),
    },
    lightGreyFontPad: {
      paddingRight: theme.spacing(19.5),
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightMedium,
    },
    lightGreyFontSub: {
      marginLeft: theme.spacing(-122.5),
      marginTop: theme.spacing(18),
    },
    tableHead: {
      fontSize: theme.typography.pxToRem(12),
    },
    dropDown: {
      '& .MuiSelect-select': {
        fontSize: theme.typography.pxToRem(12),
      },
    },
    tableBorderBottom: {
      borderBottom: 0,
    },
    othrDetailsBorderBottom: {
      borderBottom: theme.spacing(0),
      paddingBottom: theme.spacing(0),
      paddingTop: theme.spacing(1.5),
      verticalAlign: 'text-top',
    },
    hideRow: {
      display: 'none',
      fontWeight: theme.typography.fontWeightMedium,
      color: '#535353',
    },
    detailsStyle: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightMedium,
      paddingLeft: theme.spacing(12),
    },
    detailsStyleValue: {
      fontSize: theme.typography.pxToRem(12),
      paddingLeft: theme.spacing(9),
    },
    detailsStyleSynValue: {
      fontSize: theme.typography.pxToRem(12),
      paddingLeft: theme.spacing(4),
      wordBreak: 'break-all',
    },
    detailsStyleSubValue: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightMedium,
      paddingLeft: theme.spacing(0),
    },
    detailsStyleSubValueMethod: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightMedium,
      paddingLeft: theme.spacing(0),
    },
    detailsMethodStyleValue: {
      fontSize: theme.typography.pxToRem(12),
      paddingLeft: theme.spacing(8),
    },
    nextButton: {
      minWidth: 220,
      height: theme.spacing(12),
    },
    detailsKnockdownStyleValue: {
      fontSize: theme.typography.pxToRem(12),
      paddingLeft: theme.spacing(6),
    },
    tableContainer: {
      maxHeight: 895,
    },
    detailsVersionStyleValue: {
      fontSize: theme.typography.pxToRem(12),
      paddingLeft: theme.spacing(6.5),
    },
    nextButtonPadding: {
      paddingTop: theme.spacing(3),
    },
    detailsBorderBottom: {
      borderBottom: theme.spacing(0),
      paddingBottom: theme.spacing(0),
    },
    trcBorderBottom: {
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(1.5),
    },
  }
})

interface ShrnaCloneProps {
  onNext: any
  productDetails: ProductDetails
}

const ShrnaClones: React.FC<ShrnaCloneProps> = (props) => {
  const classes = useStyles()
  const router = useRouter()
  const { state, dispatch } = React.useContext(semiConfigContext)
  const [getShrnaDropDownList, { data, loading }] =
    useShrnaDropDownListLazyQuery()
  const onNext = props.onNext
  const { searchResponseJson, setIsAllChecked } = state
  const [scrollBar, setScrollBar] = React.useState(false)
  const [detailsExpanded, setDetailsExpanded] = useState(
    Array(searchResponseJson.cloneInformation.length).fill(false)
  )

  let detailsToggle: any = []
  React.useEffect(() => {
    detailsToggle = false
    setDetailsExpanded(detailsToggle)

    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.setPage])
  const handleDetailsClick = (e: React.MouseEvent, index) => {
    e.preventDefault()
    detailsToggle = Array(searchResponseJson.cloneInformation.length).fill(
      false
    )
    detailsToggle[index] = true
    if (detailsExpanded[index]) {
      detailsToggle[index] = false
    }
    setDetailsExpanded(detailsToggle)
  }

  const shrnaDropDownResult = async (obj) => {
    await getShrnaDropDownList({ variables: { input: obj } })
  }

  const setInitialState = () => {
    dispatch({ type: 'TRACE_ON_CLICK', data: true })
    dispatch({
      type: 'UPDATE_DROP_DOWN',
      data: initialState.shrnaDropDownValue,
    })
    dispatch({
      type: 'UPDATE_DROPDOWN_RESPONSE',
      data: initialState.shrnaDropDownResponseList,
    })
    dispatch({
      type: 'UPDATE_PRICE_CALL_RESPONSE',
      data: initialState.priceCallResponse,
    })
    dispatch({
      type: 'UPDATE_PRICE_CALL',
      data: initialState.priceCallData,
    })
    dispatch({
      type: 'UPDATE_SPECIFICATION_VALUES',
      data: initialState.specificationValues,
    })
  }

  const nextButton = () => {
    if (!state.priceCallData.cloneInformation.length) {
      window.scrollTo(0, 0)
    }
    const updateCountryCode = {
      ...state.shrnaDropDownValue,
      countryCode: props.productDetails.region,
      store: props.productDetails.store,
    }
    dispatch({
      type: 'UPDATE_DROP_DOWN',
      data: updateCountryCode,
    })
    onNext(shrnaDropDownResult, updateCountryCode)
    if (state.stepNo === 1) {
      dispatch({ type: 'TRACE_ON_CLICK', data: false })
    }
    dispatch({
      type: 'CHECK_ACTIVE_LINK',
      data: {
        ...state.checkActiveLink,
        specificationLink: false,
      },
    })
  }

  React.useEffect(() => {
    //window.scrollTo(0, 0)
    if (state.searchResponseJson?.cloneInformation?.length > 10) {
      setScrollBar(true)
    } else {
      setScrollBar(false)
    }
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchResponseJson.cloneInformation])

  React.useEffect(() => {
    if (data !== undefined) {
      if (!data.getShrnaDropDownList?.serverError) {
        dispatch({
          type: 'UPDATE_DROPDOWN_RESPONSE',
          data: data.getShrnaDropDownList,
        })
        const url = `${shrnaRoute.index()}?activeLink=${
          ActiveLinkType.Specifications
        }`
        if (data !== undefined) {
          router.push(url)
        }
      } else {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: SemiConfiguratorValues.InternalServerError,
        })
        window.scrollTo(0, 0)
      }
    }
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const checkSelectedShrna = (item) => {
    let isChecked = false
    state.priceCallData.cloneInformation.some((cloneInformation) => {
      if (cloneInformation.trcId === item.trcId) {
        isChecked = true
        return true
      }
      return null
    })
    return isChecked
  }

  const onChangeShrna = (item: CloneInformation) => {
    if (state.stepNo !== 1) {
      setInitialState()
    }
    let cloneInformationList: CloneInformation[] = cloneDeep(
      state.priceCallData.cloneInformation
    )

    if (!checkSelectedShrna(item) && item.trcId) {
      cloneInformationList.push(item)
    } else if (item.trcId) {
      cloneInformationList = cloneInformationList.filter(
        (o: CloneInformation) => o.trcId !== item.trcId
      )
      dispatch({ type: 'SET_IS_ALL_CHECKED', data: false })
    }
    cloneInformationList.forEach((sc) => {
      delete sc.__typename
    })
    dispatch({
      type: 'UPDATE_PRICE_CALL',
      data: {
        ...state.priceCallData,
        cloneInformation: [...cloneInformationList],
      },
    })
  }

  const onChangeShrnaAll = () => {
    if (state.stepNo !== 1) {
      setInitialState()
    }
    let cloneInformationList: CloneInformation[] = cloneDeep(
      searchResponseJson.cloneInformation
    )

    cloneInformationList.forEach((sc) => {
      delete sc.__typename
    })
    dispatch({
      type: 'SET_IS_ALL_CHECKED',
      data: setIsAllChecked ? false : true,
    })
    if (!setIsAllChecked) {
      cloneInformationList = cloneInformationList.concat(
        state.priceCallData.cloneInformation
      )
    }
    let updatedPriceCallData: any = []
    if (setIsAllChecked) {
      updatedPriceCallData = state.priceCallData.cloneInformation.filter(
        function (pcd) {
          return !state.searchResponseJson.cloneInformation.some(function (sr) {
            return pcd.trcId === sr.trcId
          })
        }
      )
    }
    dispatch({
      type: 'UPDATE_PRICE_CALL',
      data: {
        ...state.priceCallData,
        cloneInformation: [
          ...(setIsAllChecked
            ? updatedPriceCallData
            : [...cloneInformationList]),
        ],
      },
    })
  }

  React.useEffect(() => {
    let index = 0
    state.priceCallData.cloneInformation.forEach((ci) => {
      state.searchResponseJson.cloneInformation.forEach((sc) => {
        if (sc.trcId === ci.trcId) {
          index++
        }
      })
    })
    if (index === state.searchResponseJson.cloneInformation.length) {
      dispatch({ type: 'SET_IS_ALL_CHECKED', data: true })
    } else {
      dispatch({ type: 'SET_IS_ALL_CHECKED', data: false })
    }
  }, [state.searchResponseJson])

  React.useEffect(() => {
    // Added comment for avoid es-lint error
  }, [state.priceCallData.cloneInformation])

  const renderData = () => {
    const result: any = []
    cloneDeep(searchResponseJson.cloneInformation).map((item, index) => {
      result.push(
        <TableRow hover role="checkbox" tabIndex={-1} key={index}>
          <TableCell>
            <FormControlLabel
              classes={{ label: classes.fontBold }}
              control={
                <Checkbox
                  checked={checkSelectedShrna(item)}
                  value=""
                  color="primary"
                  onChange={() => onChangeShrna(item)}
                />
              }
              label={item.trcId ? item.trcId : '-'}
            />
          </TableCell>
          {item.validated === SpecificationMessages.yes ? (
            <TableCell>
              <span className={classes.lightOrangeFont}>
                <FormattedMessage
                  {...messages.SEMICONF_VALIDATED_LABEL_shrna}
                />
              </span>
            </TableCell>
          ) : (
            <TableCell></TableCell>
          )}
          <TableCell>
            <span className={classes.lightGreyFont}>
              {item.geneSymbol ? item.geneSymbol : '-'}
            </span>
          </TableCell>
          <TableCell>
            <span className={classes.lightGreyFont}>
              {item.geneId ? item.geneId : '-'}
            </span>
          </TableCell>
          <TableCell>
            <span className={classes.lightGreyFont}>
              {item.refSeq ? item.refSeq : '-'}
            </span>
          </TableCell>
          <TableCell>
            <span className={classes.lightGreyFont}>
              {item.species ? item.species : '-'}
            </span>
          </TableCell>
          <TableCell>
            <span className={classes.lightGreyFont}>
              {item.region ? item.region : '-'}
            </span>
          </TableCell>
          <TableCell className={classes.dropDown}>
            <ProductDetailsLink
              onClick={(ev) => {
                handleDetailsClick(ev, index)
              }}
              detailsExpanded={detailsExpanded[index]}
            />
          </TableCell>
        </TableRow>,

        <TableRow className={detailsExpanded[index] ? '' : classes.hideRow}>
          <TableCell colSpan={4} className={classes.detailsBorderBottom}>
            <Grid container>
              <Grid item xs={4} className={classes.detailsStyle}>
                <FormattedMessage
                  {...messages.SEMICONF_TARGET_SEQUENCE_shrna}
                />
                :
              </Grid>
              <Grid item xs={8} className={classes.detailsStyleSynValue}>
                {item.targetSeq ? item.targetSeq : '-'}
              </Grid>
            </Grid>
          </TableCell>

          <TableCell colSpan={3} className={classes.detailsBorderBottom}>
            <Grid container>
              <Grid item xs={4} className={classes.detailsStyleSubValue}>
                <FormattedMessage {...messages.SEMI_CONF_CELL_TYPE_shrna} />:
              </Grid>
              <Grid item xs={8} className={classes.detailsMethodStyleValue}>
                {item.cellType ? item.cellType : '-'}
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>,

        <TableRow className={detailsExpanded[index] ? '' : classes.hideRow}>
          <TableCell colSpan={4} className={classes.othrDetailsBorderBottom}>
            <Grid container>
              <Grid item xs={4} className={classes.detailsStyle}>
                <FormattedMessage {...messages.SEMICONF_GENE_SYN_shrna} />:
              </Grid>
              <Grid item xs={8} className={classes.detailsStyleSynValue}>
                {item.synonyms && item.synonyms.length
                  ? item.synonyms.join(', ')
                  : '-'}
              </Grid>
            </Grid>
          </TableCell>
          <TableCell colSpan={3} className={classes.othrDetailsBorderBottom}>
            <Grid container>
              <Grid item xs={4} className={classes.detailsStyleSubValueMethod}>
                <FormattedMessage {...messages.SEMI_CONF_METHOD_shrna} />:
              </Grid>
              <Grid item xs={8} className={classes.detailsMethodStyleValue}>
                {item.method ? item.method : '-'}
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>,

        <TableRow className={detailsExpanded[index] ? '' : classes.hideRow}>
          <TableCell
            colSpan={4}
            className={classes.othrDetailsBorderBottom}
          ></TableCell>
          <TableCell colSpan={3} className={classes.othrDetailsBorderBottom}>
            <Grid container>
              <Grid item xs={4} className={classes.detailsStyleSubValue}>
                <FormattedMessage {...messages.SEMI_CONF_KNOCKDOWN_shrna} />:
              </Grid>
              <Grid item xs={8} className={classes.detailsMethodStyleValue}>
                {item.knockdown
                  ? Number(item.knockdown) !== 0
                    ? item.knockdown + SpecificationMessages.percentage
                    : '-'
                  : '-'}
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>,

        <TableRow className={detailsExpanded[index] ? '' : classes.hideRow}>
          <TableCell
            colSpan={4}
            className={classes.trcBorderBottom}
          ></TableCell>
          <TableCell colSpan={4} className={classes.trcBorderBottom}>
            <Grid container>
              <Grid item xs={3} className={classes.detailsStyleSubValue}>
                <FormattedMessage {...messages.SEMI_CONF_VERSION_shrna} />:
              </Grid>
              <Grid item xs={9} className={classes.detailsVersionStyleValue}>
                {item.trcVersion ? item.trcVersion : '-'}
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>
      )
      return item
    })
    return result
  }
  return (
    <>
      <Formik
        initialValues={{ DetailsSection: 'Details' }}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            actions.setSubmitting(false)
          }, 1000)
        }}
      >
        <TableContainer className={scrollBar ? classes.tableContainer : ''}>
          <Paper className={classes.root}>
            <Table stickyHeader aria-label="sticky table">
              <TableRow>
                {columns.map((column, index) => {
                  if (column.id === 'TRCCloneId')
                    return (
                      <TableCell
                        key={index}
                        align={column.align}
                        style={{ minWidth: column.minWidth }}
                        className={classes.tableBorderBottom}
                      >
                        <FormControlLabel
                          classes={{ label: classes.fontBold }}
                          control={
                            <Checkbox
                              value=""
                              color="primary"
                              checked={setIsAllChecked}
                              onChange={() => onChangeShrnaAll()}
                            />
                          }
                          label={column.label}
                        />
                      </TableCell>
                    )
                  else
                    return (
                      <TableCell
                        key={index}
                        align={column.align}
                        style={{ minWidth: column.minWidth }}
                        className={classes.tableBorderBottom}
                      >
                        <strong className={classes.tableHead}>
                          {column.label}
                        </strong>
                      </TableCell>
                    )
                })}
              </TableRow>
              <TableBody>{renderData()}</TableBody>
            </Table>
          </Paper>
        </TableContainer>
      </Formik>

      <div>
        <Grid container>
          <Grid item xs={12}>
            <PaginationForShrna />
          </Grid>
          <Grid item xs={12}>
            <Button
              color="primary"
              variant="contained"
              className={classes.nextButton}
              onClick={nextButton}
            >
              <FormattedMessage {...messages.CONTINUE} />
            </Button>
          </Grid>
        </Grid>
      </div>
      {loading && <CircleLoader />}
    </>
  )
}

export default ShrnaClones
